import { useRef } from 'react'
import { useMemoizedFn } from 'ahooks'

/** 数据采集选项 */
export interface DataCollectorOptions<D = unknown> {
    /** 采集间隔 */
    interval?: number

    /**
     * 最大采集数量，当前采集到的数据队列大小达到 `maxCount` 时，将触发 `onIntervalEnd` 回调。
     * @default 10
     */
    maxCount?: number

    /** 采集数量队列大小达到 `maxCount` 时或者采集间隔到达 `interval` 时触发的回调 */
    onIntervalEnd?: (data: D[]) => void
}

/** 默认选项 */
const DEFAULT_COLLECTOR_OPTIONS: Required<DataCollectorOptions> = {
    interval: 500,
    maxCount: 10,
    onIntervalEnd: () => undefined
}

/**
 * 每间隔 `interval` 时间调用一次 `onIntervalEnd` 回调
 * 将这段时间里 `collect` 方法收集到的数据传递给 `onIntervalEnd`
 *
 * @param options - 选项
 */
export function useDataCollector<D = unknown>(options: DataCollectorOptions<D>) {
    const { interval, maxCount, onIntervalEnd } = { ...DEFAULT_COLLECTOR_OPTIONS, ...options }

    /** 数据采集队列 */
    const queueRef = useRef<D[]>([])

    /** 定时器引用 */
    const timerRef = useRef<number | undefined>(undefined)

    /** 采集方法 */
    const collect = useMemoizedFn((data: D) => {
        queueRef.current.push(data)

        if (queueRef.current.length >= maxCount) {
            onIntervalEnd?.(queueRef.current)
            queueRef.current = []
            if (timerRef.current !== undefined) {
                window.clearTimeout(timerRef.current)
            }
            timerRef.current = undefined
        }

        if (timerRef.current === undefined) {
            timerRef.current = window.setTimeout(() => {
                onIntervalEnd?.(queueRef.current)
                queueRef.current = []
                timerRef.current = undefined
            }, interval)
        }
    })

    return { collect }
}
